/**
 * Best-effort decode of the access-token JWT payload — UI CONVENIENCE ONLY.
 *
 * At startup we want to paint the real app shell (sidebar/topbar/nav) the instant
 * a persisted token is found, WITHOUT waiting for the `/auth/me` round-trip. The
 * backend signs the access token with the full user payload (`sub`, `email`,
 * `name`, `roles`, `permissions` — see api `auth.service.ts#signAccess`), so we
 * can reconstruct a usable `CurrentUser` locally to render that shell optimistically.
 *
 * SECURITY: this NEVER authorizes anything. The signature is NOT verified and the
 * payload may be stale or expired — it only decides what the UI *draws*. Every
 * real permission is still enforced by the API (docs/security.md), and `/auth/me`
 * validates the session in the background and overwrites this optimistic user.
 *
 * The token is NEVER logged. Every failure path returns `null` (an opaque or
 * malformed token simply disables the optimistic shell and falls back to the
 * blocking restore) — this function must never throw.
 */

import type { CurrentUser, RoleName } from "./permissions";

/** Decode one base64url segment to a UTF-8 string. Returns null on any problem. */
function decodeBase64UrlToString(segment: string): string | null {
  try {
    let b64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const remainder = b64.length % 4;
    if (remainder === 1) return null; // not a valid base64 length
    if (remainder === 2) b64 += "==";
    else if (remainder === 3) b64 += "=";

    if (typeof atob !== "function") return null;
    const binary = atob(b64);
    // atob yields a binary (latin1) string; decode as UTF-8 so accented names
    // ("Amélie", "Propriétaire") survive the round-trip.
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

/** Parse the JWT payload segment (index 1) into a plain object, or null. */
function decodeJwtPayload(token: string | null | undefined): Record<string, unknown> | null {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null; // header.payload.signature
  const json = decodeBase64UrlToString(parts[1] ?? "");
  if (!json) return null;
  try {
    const parsed: unknown = JSON.parse(json);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function toStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  return value.filter((item): item is string => typeof item === "string");
}

/**
 * Reconstruct a `CurrentUser` from an access-token JWT for optimistic rendering.
 * Returns null when the token is opaque, malformed, or missing the fields the UI
 * needs — the caller then falls back to the blocking (validate-first) restore.
 */
export function decodeUserFromAccessToken(token: string | null | undefined): CurrentUser | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  const { sub, email, name } = payload;
  if (typeof sub !== "string" || sub.length === 0) return null;
  if (typeof email !== "string") return null;
  if (typeof name !== "string") return null;

  const roles = toStringArray(payload.roles);
  const permissions = toStringArray(payload.permissions);
  if (!roles || !permissions) return null;

  return {
    id: sub,
    name,
    email,
    roles: roles as RoleName[],
    permissions,
  };
}
